import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Save, X } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import BloqueAtributos from './BloqueAtributos';
import SelectorCategoria from './SelectorCategoria';
import { faltanObligatorios, paraGuardar } from './fichaAtributos';

/* =====================================================================
   LA FICHA QUE SE ADAPTA · CATÁLOGO VIVO F2 (edición)
   =====================================================================
   Nombre → categoría (con buscador y ruta) → los datos de la plantilla.
   Cambiar de categoría repinta los datos sin perder lo tecleado (la
   memoria vive en `fichaAtributos.js`). Guardar se apaga SOLO por
   obligatorios que faltan, y dice cuáles.

   Se abre desde «Fichas incompletas» (Catálogo). El alta y la edición de
   Productos y la Ficha previa de alta por escaneo adoptan el BLOQUE por
   parche (CATALOGO-VIVO-PARCHES.md): sus formularios ya piden nombre,
   unidad, ubicación y precio, y eso no se duplica aquí (R-CV9).
   ===================================================================== */
const mensaje = (e) => (e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');

export default function FichaProducto({ idProducto, onGuardado, onCerrar }) {
  const [leida, setLeida] = useState(null);
  const [idCategoria, setIdCategoria] = useState(null);
  const [memoria, setMemoria] = useState({});
  const [plantilla, setPlantilla] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);

  const cargar = useCallback(() => {
    api.get(`/catalogo-vivo/catalogo/productos/${idProducto}/atributos`)
      .then((r) => { setError(null); setLeida(r); setIdCategoria(r.producto.id_categoria); })
      .catch((e) => setError(mensaje(e)));
  }, [idProducto]);
  useEffect(() => { cargar(); }, [cargar]);

  const faltan = faltanObligatorios(plantilla, memoria);
  const cambioCategoria = leida && idCategoria !== leida.producto.id_categoria;

  async function guardar() {
    setGuardando(true);
    setError(null);
    setHecho(null);
    try {
      if (cambioCategoria) await api.patch(`/productos/${idProducto}`, { id_categoria: idCategoria });
      const valores = paraGuardar(plantilla, memoria);
      const r = Object.keys(valores).length || cambioCategoria
        ? await api.put(`/catalogo-vivo/catalogo/productos/${idProducto}/atributos`, { valores })
        : leida;
      setLeida(r);
      setMemoria({});
      setHecho('Listo: la ficha quedó guardada.');
      onGuardado?.(r);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setGuardando(false);
    }
  }

  if (error && !leida) return <p role="alert" className="text-sm text-red-700">{error}</p>;
  if (!leida) return <p className="text-sm text-gray-600 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo la ficha…</p>;
  return (
    <section className="rounded-xl border border-gray-200 p-3 space-y-4" data-catalogo-vivo="ficha-producto">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 break-words">{leida.producto.nombre}</h3>
          <p className="text-xs text-gray-500 font-mono">{leida.producto.sku}</p>
        </div>
        {onCerrar && (
          <button type="button" onClick={onCerrar} className="min-h-11 inline-flex items-center gap-1 rounded-xl border border-gray-300 px-3 text-sm">
            <X className="h-4 w-4" aria-hidden="true" /> Cerrar
          </button>
        )}
      </div>
      <Ayuda id="catalogo_vivo.ficha.pantalla" />
      <SelectorCategoria valor={idCategoria} onElegir={(id) => setIdCategoria(id)} />
      <BloqueAtributos idCategoria={idCategoria} memoria={memoria} onCambio={setMemoria}
        atributosGuardados={leida.atributos} onPlantilla={setPlantilla} />
      {leida.otros?.length > 0 && (
        <p className="text-xs text-gray-600">
          También tiene guardado, de otra categoría: {leida.otros.map((o) => `${o.nombre}: ${o.valor}`).join(' · ')}. No se borra.
        </p>
      )}
      {hecho && <p role="status" className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <button type="button" disabled={faltan.length > 0 || guardando} onClick={guardar}
        className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4 disabled:opacity-60">
        {guardando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Save className="h-5 w-5" aria-hidden="true" />}
        Guardar
      </button>
      {faltan.length > 0 && <p className="text-xs text-amber-800" data-motivo="guardar">Para guardar falta: {faltan.join(', ')}.</p>}
    </section>
  );
}
